import type {
  CadenceKind,
  CadenceMeta,
  CompanyProfile,
  ResearchConfidence,
  WorkspaceTeamBlueprint,
} from "@yagni/shared";
import {
  BriefcaseBusiness,
  Check,
  CircleDot,
  ExternalLink,
  Plug,
  ReceiptText,
  ShieldCheck,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  Textarea,
} from "../../ui/index.js";
import {
  CONNECTION_OPTIONS,
  cadenceLabel,
  confidenceVariant,
  connectorLabel,
  type TeamDraft,
} from "./model.js";

function safeHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return "Public source";
  }
}

function evidenceLabel(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.pathname.replace(/^\/$/, "home").split("/").filter(Boolean).slice(-1)[0] ?? parsed.hostname;
  } catch {
    return "source";
  }
}

type ConnectionReviewStatus = "Connected" | "Needed" | "Choose after install";

function connectionReviewStatus(
  connectorId: string,
  connectedConnectorIds: readonly string[],
): ConnectionReviewStatus {
  if (connectedConnectorIds.includes(connectorId)) return "Connected";
  return CONNECTION_OPTIONS.find((option) => option.id === connectorId)?.personal
    ? "Choose after install"
    : "Needed";
}

function joinLabels(labels: readonly string[]): string {
  if (labels.length < 2) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

function connectionReviewSummary(
  connectorIds: readonly string[],
  connectedConnectorIds: readonly string[],
): string {
  const grouped = new Map<ConnectionReviewStatus, string[]>([
    ["Connected", []],
    ["Needed", []],
    ["Choose after install", []],
  ]);
  for (const id of connectorIds) {
    grouped.get(connectionReviewStatus(id, connectedConnectorIds))!.push(connectorLabel(id));
  }
  const parts: string[] = [];
  const connected = grouped.get("Connected")!;
  const needed = grouped.get("Needed")!;
  const personal = grouped.get("Choose after install")!;
  if (connected.length > 0) parts.push(`Connected now: ${joinLabels(connected)}.`);
  if (needed.length > 0) parts.push(`Waiting for: ${joinLabels(needed)}.`);
  if (personal.length > 0) parts.push(`Choose personal access after install: ${joinLabels(personal)}.`);
  return parts.join(" ");
}

export function ConfidenceBadge({ confidence }: { confidence: ResearchConfidence }) {
  return (
    <Badge variant={confidenceVariant(confidence)} size="xs" className="capitalize">
      {confidence} confidence
    </Badge>
  );
}

export function EvidencePanel({ profile }: { profile: CompanyProfile }) {
  return (
    <Card variant="outlined" padding="md" className="mt-4 space-y-4 bg-[var(--accent-subtle)]">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CircleDot className="h-4 w-4 text-[var(--accent)]" aria-hidden="true" />
          <h3 className="text-sm font-semibold">Research receipts</h3>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--text-tertiary)]">
          {profile.sources.length} public {profile.sources.length === 1 ? "source" : "sources"}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {profile.sources.map((source) => (
          <a
            key={source.url}
            href={source.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--paper-card)] px-2.5 py-1 text-xs text-[var(--text-secondary)] hover:border-[var(--border-hover)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
          >
            <span className="truncate">{source.title ?? safeHostname(source.url)}</span>
            <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
          </a>
        ))}
      </div>
      {profile.hiringSignals.length > 0 && (
        <div className="space-y-2 border-t border-[var(--border-subtle)] pt-3">
          <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--text-tertiary)]">
            <BriefcaseBusiness className="h-3.5 w-3.5" aria-hidden="true" /> Hiring signals
          </p>
          {profile.hiringSignals.slice(0, 4).map((signal) => (
            <p key={`${signal.sourceUrl}:${signal.roleTitle}`} className="text-xs leading-5 text-[var(--text-secondary)]">
              <strong className="font-semibold text-[var(--text-primary)]">{signal.roleTitle}</strong>
              {" — "}{signal.summary}
            </p>
          ))}
        </div>
      )}
    </Card>
  );
}

interface TeamCardProps {
  candidate: WorkspaceTeamBlueprint["teams"][number];
  draft: TeamDraft;
  expanded: boolean;
  onToggle: () => void;
  onExpand: () => void;
  onChange: (patch: Partial<TeamDraft>) => void;
  connectedConnectorIds?: readonly string[];
}

export function TeamCard({
  candidate,
  draft,
  expanded,
  onToggle,
  onExpand,
  onChange,
  connectedConnectorIds = [],
}: TeamCardProps) {
  const starterPlays = candidate.recipe.starterPlays;
  const evidenceUrls = candidate.evidenceUrls.slice(0, 3);
  const startingRules = draft.playbookRules
    .split("\n")
    .map((rule) => rule.trim())
    .filter(Boolean);

  return (
    <Card
      data-testid={`team-card-${candidate.id}`}
      padding="none"
      className={`overflow-hidden transition-colors motion-reduce:transition-none ${
        draft.enabled
          ? "border-[var(--glass-border)] bg-[var(--glass-bg)] shadow-[var(--shadow-staged)] backdrop-blur-lg"
          : "border-dashed border-[var(--dash-border)] bg-transparent shadow-none opacity-75"
      }`}
    >
      <div className="p-4 md:p-5">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={draft.enabled}
            onCheckedChange={onToggle}
            aria-label={`Enable ${draft.teamName} Team`}
            className="mt-1"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-[var(--font-display)] text-lg font-semibold tracking-[-0.02em]">
                    {draft.teamName} Team
                  </h3>
                  {candidate.recommended && <Badge variant="amber" size="xs">Recommended</Badge>}
                  <ConfidenceBadge confidence={candidate.confidence} />
                </div>
                <p className="mt-1 max-w-2xl text-sm leading-5 text-[var(--text-secondary)]">
                  {candidate.rationale}
                </p>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onExpand}
                aria-expanded={expanded}
                aria-controls={`team-editor-${candidate.id}`}
              >
                {expanded ? "Close" : "Edit"} {draft.teamName} Team
              </Button>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="warning" size="xs">Supervised</Badge>
              <span className="text-[var(--text-tertiary)]">{cadenceLabel(draft.cadenceKind)}</span>
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div className="rounded-lg bg-[var(--glass-inner-bg)] p-3">
                <div className="mb-1 flex items-center gap-2 text-xs font-semibold text-[var(--text-primary)]">
                  <ShieldCheck className="h-3.5 w-3.5 text-[var(--accent)]" aria-hidden="true" />
                  Training authority
                </div>
                <p className="text-xs leading-5 text-[var(--text-secondary)]">
                  This Team investigates and drafts. Nothing ships without your approval.
                </p>
              </div>
              <div className="rounded-lg bg-[var(--glass-inner-bg)] p-3">
                <div className="mb-1 flex items-center gap-2 text-xs font-semibold text-[var(--text-primary)]">
                  <ReceiptText className="h-3.5 w-3.5 text-[var(--accent)]" aria-hidden="true" />
                  Starter Work
                </div>
                {starterPlays.length > 0 ? (
                  <>
                    <p className="text-[11px] leading-4 text-[var(--text-tertiary)]">
                      {draft.enabled
                        ? "Creating this Team prepares every item below as proposed Work. Each still needs approval."
                        : "If you enable this Team, every item below will be prepared as proposed Work."}
                    </p>
                    <ul aria-label={`${draft.teamName} Starter Work`} className="mt-2 space-y-2">
                      {starterPlays.map((play, index) => (
                        <li key={`${index}:${play.title}`} className="border-t border-[var(--border-subtle)] pt-2 first:border-t-0 first:pt-0">
                          <p className="text-xs font-medium">{play.title}</p>
                          <p className="mt-1 text-xs leading-5 text-[var(--text-secondary)]">{play.description}</p>
                          <p className="mt-1 text-xs leading-5 text-[var(--text-tertiary)]">
                            <strong className="font-medium text-[var(--text-secondary)]">Receipt:</strong> {play.receipt}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-xs text-[var(--text-tertiary)]">No Starter Work was proposed for this Team.</p>
                )}
              </div>
            </div>

            {startingRules.length > 0 && (
              <div className="mt-3 rounded-lg border border-[var(--border)] bg-[var(--glass-inner-bg)] p-3">
                <div className="mb-1 flex items-center gap-2 text-xs font-semibold text-[var(--text-primary)]">
                  <ShieldCheck className="h-3.5 w-3.5 text-[var(--accent)]" aria-hidden="true" />
                  Starting Playbook
                </div>
                <p className="text-[11px] leading-4 text-[var(--text-tertiary)]">
                  Creating this Team activates these reviewed rules.
                </p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-xs leading-5 text-[var(--text-secondary)]">
                  {startingRules.map((rule, index) => <li key={`${index}:${rule}`}>{rule}</li>)}
                </ul>
              </div>
            )}

            {draft.expectedConnectorIds.length > 0 ? (
              <p className="mt-3 flex items-start gap-2 text-xs leading-5 text-[var(--text-secondary)]">
                <Plug className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--accent)]" aria-hidden="true" />
                {connectionReviewSummary(draft.expectedConnectorIds, connectedConnectorIds)}
              </p>
            ) : (
              <p className="mt-3 flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                <Check className="h-3.5 w-3.5 text-[var(--accent-active)]" aria-hidden="true" />
                No Connection is required to start this Team.
              </p>
            )}

            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--text-tertiary)]">
              <span>Evidence</span>
              {evidenceUrls.map((url) => (
                <a key={url} href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline decoration-[var(--border-hover)] underline-offset-2 hover:text-[var(--text-primary)]">
                  {evidenceLabel(url)}
                  <ExternalLink className="h-2.5 w-2.5" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {expanded && (
        <div id={`team-editor-${candidate.id}`} className="border-t border-[var(--border)] bg-[var(--accent-subtle)] p-4 md:p-5">
          <div className="grid gap-4 md:grid-cols-2">
            <FormField label="Team name" htmlFor={`team-name-${candidate.id}`} required>
              <Input
                id={`team-name-${candidate.id}`}
                value={draft.teamName}
                maxLength={120}
                onChange={(event) => onChange({ teamName: event.target.value })}
              />
            </FormField>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <FormField label="Responsibilities" htmlFor={`team-responsibilities-${candidate.id}`} hint="One per line" required>
              <Textarea
                id={`team-responsibilities-${candidate.id}`}
                autosize
                rows={4}
                value={draft.responsibilities}
                onChange={(event) => onChange({ responsibilities: event.target.value })}
              />
            </FormField>
            <FormField label="Starting Playbook rules" htmlFor={`team-playbook-${candidate.id}`} hint="One per line; applied when you create this Team">
              <Textarea
                id={`team-playbook-${candidate.id}`}
                autosize
                rows={4}
                value={draft.playbookRules}
                onChange={(event) => onChange({ playbookRules: event.target.value })}
              />
            </FormField>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <CadenceEditor draft={draft} onChange={onChange} />
            <ConnectionEditor
              draft={draft}
              connectedConnectorIds={connectedConnectorIds}
              onChange={onChange}
            />
          </div>
        </div>
      )}
    </Card>
  );
}

function CadenceEditor({ draft, onChange }: { draft: TeamDraft; onChange: (patch: Partial<TeamDraft>) => void }) {
  const time = `${String(draft.cadenceMeta.hour ?? 9).padStart(2, "0")}:${String(draft.cadenceMeta.minute ?? 0).padStart(2, "0")}`;
  const timed = draft.cadenceKind === "daily" || draft.cadenceKind === "weekly";
  return (
    <fieldset className="rounded-lg border border-[var(--border)] bg-[var(--paper-card)] p-3">
      <legend className="px-1 text-xs font-semibold">Rhythm</legend>
      <label className="text-xs text-[var(--text-secondary)]" htmlFor={`team-cadence-${draft.id}`}>Filing rhythm</label>
      <select
        id={`team-cadence-${draft.id}`}
        className="mt-1 block h-9 w-full rounded-md border border-[var(--border)] bg-[var(--bg-input)] px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
        value={draft.cadenceKind}
        onChange={(event) => onChange({ cadenceKind: event.target.value as CadenceKind })}
      >
        <option value="editions">Morning, midday, and evening</option>
        <option value="daily">Daily</option>
        <option value="weekly">Weekly</option>
        <option value="as_it_happens">As it happens</option>
      </select>
      {timed && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          {draft.cadenceKind === "weekly" && (
            <label className="text-xs text-[var(--text-secondary)]">
              Day
              <select
                aria-label="Rhythm day"
                className="mt-1 block h-9 w-full rounded-md border border-[var(--border)] bg-[var(--bg-input)] px-2 text-sm"
                value={draft.cadenceMeta.weekday ?? "mon"}
                onChange={(event) => onChange({
                  cadenceMeta: {
                    ...draft.cadenceMeta,
                    weekday: event.target.value as NonNullable<CadenceMeta["weekday"]>,
                  },
                })}
              >
                {(["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const).map((day) => (
                  <option key={day} value={day}>{day.toUpperCase()}</option>
                ))}
              </select>
            </label>
          )}
          <label className={`text-xs text-[var(--text-secondary)] ${draft.cadenceKind === "daily" ? "col-span-2" : ""}`}>
            Time
            <Input
              aria-label="Rhythm time"
              type="time"
              inputSize="sm"
              className="mt-1"
              value={time}
              onChange={(event) => {
                const [hour, minute] = event.target.value.split(":").map(Number);
                onChange({ cadenceMeta: { ...draft.cadenceMeta, hour, minute } });
              }}
            />
          </label>
        </div>
      )}
    </fieldset>
  );
}

function ConnectionEditor({
  draft,
  connectedConnectorIds,
  onChange,
}: {
  draft: TeamDraft;
  connectedConnectorIds: readonly string[];
  onChange: (patch: Partial<TeamDraft>) => void;
}) {
  const available = CONNECTION_OPTIONS.filter((option) => !draft.expectedConnectorIds.includes(option.id));
  return (
    <fieldset className="rounded-lg border border-[var(--border)] bg-[var(--paper-card)] p-3">
      <legend className="px-1 text-xs font-semibold">Expected Connections</legend>
      <div className="flex flex-wrap gap-1.5">
        {draft.expectedConnectorIds.map((id) => {
          const status = connectionReviewStatus(id, connectedConnectorIds);
          return (
          <span key={id} className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] bg-[var(--bg-base)] py-1 pl-2.5 pr-1 text-xs">
            <span>{connectorLabel(id)}</span>
            <span className="rounded-full bg-[var(--paper-card)] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.08em] text-[var(--text-tertiary)]">
              {status}
            </span>
            <button
              type="button"
              className="grid h-5 w-5 place-items-center rounded-full text-[var(--text-tertiary)] hover:bg-[rgba(var(--glass-rgb),0.68)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
              onClick={() => onChange({ expectedConnectorIds: draft.expectedConnectorIds.filter((candidate) => candidate !== id) })}
              aria-label={`Remove ${connectorLabel(id)} from ${draft.teamName}`}
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          </span>
          );
        })}
        {draft.expectedConnectorIds.length === 0 && (
          <span className="text-xs text-[var(--text-tertiary)]">None required</span>
        )}
      </div>
      {available.length > 0 && (
        <select
          aria-label={`Add a Connection for ${draft.teamName}`}
          className="mt-3 block h-9 w-full rounded-md border border-[var(--border)] bg-[var(--bg-input)] px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
          value=""
          onChange={(event) => {
            if (!event.target.value) return;
            onChange({ expectedConnectorIds: [...draft.expectedConnectorIds, event.target.value] });
          }}
        >
          <option value="">Add a Connection…</option>
          {available.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}{option.personal ? " (personal)" : ""}
            </option>
          ))}
        </select>
      )}
      <p className="mt-2 text-[11px] leading-4 text-[var(--text-tertiary)]">
        Selected shared Connections attach when already active here; anything missing stays a request. Personal accounts are never attached automatically.
      </p>
    </fieldset>
  );
}
